import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import StagesEditor from "@/components/StagesEditor";
import DangerZone from "@/components/DangerZone";
import { fmtDate } from "@/lib/meta";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, CheckCircle2, XCircle, Plus, Trash2, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const TYPES = [
  { key: "main", label: "Main sheet" }, { key: "whatsapp", label: "WhatsApp form" },
  { key: "facebook", label: "Facebook / Meta" }, { key: "ai_screening", label: "AI screening" },
  { key: "consultant", label: "Consultants" }, { key: "owner", label: "Property owners" },
];

export default function Settings() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saJson, setSaJson] = useState("");
  const [busy, setBusy] = useState(false);
  const [src, setSrc] = useState({ spreadsheet: "", worksheet_title: "", sheet_type: "main", label: "" });

  const load = () => { setLoading(true); api.get("/integrations/status").then(({ data }) => setStatus(data)).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  const saveSA = async () => { setBusy(true); try { await api.post("/integrations/service-account", { json_text: saJson }); setSaJson(""); load(); toast.success("Service account connected"); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); } };
  const addSource = async () => { setBusy(true); try { await api.post("/integrations/sheets/sources", src); setSrc({ spreadsheet: "", worksheet_title: "", sheet_type: "main", label: "" }); load(); toast.success("Sheet source added"); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); } };
  const delSource = async (id) => { try { await api.delete(`/integrations/sheets/sources/${id}`); load(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  const syncNow = async () => { setBusy(true); try { const { data } = await api.post("/integrations/sync-now"); toast.success("Sync complete"); load(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); } };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-[#0047FF]" /></div>;

  return (
    <div>
      <PageHeader title="Settings" subtitle="Google Sheets sync and pipeline stage configuration.">
        <Button size="sm" variant="outline" onClick={syncNow} disabled={busy || !status?.connected} data-testid="sync-now-btn"><RefreshCw className="h-4 w-4" /> Sync now</Button>
      </PageHeader>

      <div className="px-8 py-6 space-y-6 max-w-3xl">
        <div className="bg-white border border-gray-200 rounded-lg p-5">
          <div className="flex items-center gap-2 mb-3">
            {status?.connected ? <CheckCircle2 className="h-5 w-5 text-green-600" /> : <XCircle className="h-5 w-5 text-gray-400" />}
            <span className="font-heading font-bold">Google Service Account</span>
          </div>
          {status?.connected ? (
            <div className="text-sm text-gray-600">Connected as <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">{status.service_account_email}</code>. Share each sheet with this email (Viewer).</div>
          ) : (
            <div className="space-y-2">
              <div className="text-sm text-gray-500">Paste your service account JSON to connect.</div>
              <Textarea rows={5} value={saJson} onChange={(e) => setSaJson(e.target.value)} placeholder='{"type":"service_account", ...}' className="font-mono text-xs" data-testid="sa-json-input" />
              <Button size="sm" onClick={saveSA} disabled={busy || !saJson.trim()} data-testid="save-sa-btn">Connect</Button>
            </div>
          )}
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-5">
          <div className="font-heading font-bold mb-3">Connected sheets</div>
          <div className="space-y-2 mb-4">
            {(status?.sheet_sources || []).length === 0 ? <div className="text-sm text-gray-400">No sheets connected yet.</div> :
              status.sheet_sources.map((s) => (
                <div key={s.id} className="flex items-center justify-between border border-gray-200 rounded p-2.5" data-testid={`sheet-source-${s.id}`}>
                  <div className="text-sm">
                    <span className="font-medium">{s.label || s.sheet_type}</span>
                    <span className="text-xs text-gray-500 ml-2">{s.worksheet_title || "Sheet1"} · {s.sheet_type}</span>
                    {s.last_sync_at && <div className="text-[11px] text-gray-400">Last: {s.last_sync_status} · {fmtDate(s.last_sync_at)}{s.last_error ? ` · ${s.last_error}` : ""}</div>}
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => delSource(s.id)} data-testid={`del-source-${s.id}`}><Trash2 className="h-3.5 w-3.5 text-rose-500" /></Button>
                </div>
              ))}
          </div>
          <div className="border-t border-gray-200 pt-4 grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Spreadsheet URL or ID</Label><Input value={src.spreadsheet} onChange={(e) => setSrc({ ...src, spreadsheet: e.target.value })} className="mt-1" data-testid="src-spreadsheet" /></div>
            <div><Label>Worksheet tab (optional)</Label><Input value={src.worksheet_title} onChange={(e) => setSrc({ ...src, worksheet_title: e.target.value })} className="mt-1" data-testid="src-tab" /></div>
            <div><Label>Format</Label>
              <Select value={src.sheet_type} onValueChange={(v) => setSrc({ ...src, sheet_type: v })}>
                <SelectTrigger className="mt-1" data-testid="src-type"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{TYPES.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label>Label</Label><Input value={src.label} onChange={(e) => setSrc({ ...src, label: e.target.value })} className="mt-1" data-testid="src-label" /></div>
            <div className="col-span-2"><Button size="sm" onClick={addSource} disabled={busy || !src.spreadsheet} data-testid="add-source-btn"><Plus className="h-4 w-4" /> Add sheet</Button></div>
          </div>
        </div>

        <div className="text-xs text-gray-400">Leads sync automatically every day at 2:00 AM IST, then market intelligence recomputes.</div>
        <StagesEditor />
        <DangerZone />
      </div>
    </div>
  );
}
